import {PayloadStrategy} from "@/components/audit/adjustment/adapters/types";
import {
    DueFromToDirectorsWorkingPaper,
    GainLossDisposalWorkingPaper,
    GainLossExchangeWorkingPaper
} from "@/services/auditService/interface/dynamic";

// Due From/ To Directors strategy (JX1): ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const dueFromToDirectorsStrategy: PayloadStrategy = {
    buildPayload: ({ prev, ctx }) => {
        const p = (prev || {}) as Partial<DueFromToDirectorsWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const dueFromToDirRow = addAnnualId(Array.isArray(p.dueFromToDirRow) ? p.dueFromToDirRow : []);
        
        const next: any = {};
        if (dueFromToDirRow.length > 0) next.dueFromToDirRow = dueFromToDirRow;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
}

// Gain/ Loss Exchange strategy (RX10): ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const gainLossExchangeStrategy: PayloadStrategy = {
    buildPayload: ({ prev, ctx }) => {
        const p = (prev || {}) as Partial<GainLossExchangeWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const gainExchange = addAnnualId(Array.isArray(p.gainExchange) ? p.gainExchange : []);
        const lossExchange = addAnnualId(Array.isArray(p.lossExchange) ? p.lossExchange : []);
        
        const next: any = {};
        if (gainExchange.length > 0) next.gainExchange = gainExchange;
        if (lossExchange.length > 0) next.lossExchange = lossExchange;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
}

// Gain/Loss Disposal strategy (RX12): supports new GainLossDisposalWorkingPaper format.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const gainLossDisposalStrategy: PayloadStrategy = {
    buildPayload: ({ prev, ctx }) => {
        const p = (prev || {}) as Partial<GainLossDisposalWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[] | undefined): T[] | undefined => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const locationOfPropertyDisRow = addAnnualId(Array.isArray(p.locationOfPropertyDisRow) ? p.locationOfPropertyDisRow : undefined);
        const commercialAllowanceDisRow = addAnnualId(Array.isArray(p.commercialAllowanceDisRow) ? p.commercialAllowanceDisRow : undefined);
        const industrialAllowanceDisRow = addAnnualId(Array.isArray(p.industrialAllowanceDisRow) ? p.industrialAllowanceDisRow : undefined);
        const furnitureFixtureDisRow = addAnnualId(Array.isArray(p.furnitureFixtureDisRow) ? p.furnitureFixtureDisRow : undefined);
        const computerHardSoftDisRow = addAnnualId(Array.isArray(p.computerHardSoftDisRow) ? p.computerHardSoftDisRow : undefined);
        const fullPayMotorDisRow = addAnnualId(Array.isArray(p.fullPayMotorDisRow) ? p.fullPayMotorDisRow : undefined);
        const hirePurchaseMotorDisRow = addAnnualId(Array.isArray(p.hirePurchaseMotorDisRow) ? p.hirePurchaseMotorDisRow : undefined);
        const machineryDisRow = addAnnualId(Array.isArray(p.machineryDisRow) ? p.machineryDisRow : undefined);
        const liftEscalatorDisRow = addAnnualId(Array.isArray(p.liftEscalatorDisRow) ? p.liftEscalatorDisRow : undefined);
        const equipmentDisRow = addAnnualId(Array.isArray(p.equipmentDisRow) ? p.equipmentDisRow : undefined);
        const intangibleRow = addAnnualId(Array.isArray(p.intangibleRow) ? p.intangibleRow : undefined);
        const subsidiaryRow = addAnnualId(Array.isArray(p.subsidiaryRow) ? p.subsidiaryRow : undefined);
        const associateRow = addAnnualId(Array.isArray(p.associateRow) ? p.associateRow : undefined);
        const taxiLicenseDisRow = addAnnualId(Array.isArray(p.taxiLicenseDisRow) ? p.taxiLicenseDisRow : undefined);
        const fullPayTaxiCabDisRow = addAnnualId(Array.isArray(p.fullPayTaxiCabDisRow) ? p.fullPayTaxiCabDisRow : undefined);
        const hirePurchaseTaxiCabDisRow = addAnnualId(Array.isArray(p.hirePurchaseTaxiCabDisRow) ? p.hirePurchaseTaxiCabDisRow : undefined);
        const trustsRow = addAnnualId(Array.isArray(p.trustsRow) ? p.trustsRow : undefined);
        
        const next: any = {};
        if (locationOfPropertyDisRow && locationOfPropertyDisRow.length > 0) next.locationOfPropertyDisRow = locationOfPropertyDisRow;
        if (commercialAllowanceDisRow && commercialAllowanceDisRow.length > 0) next.commercialAllowanceDisRow = commercialAllowanceDisRow;
        if (industrialAllowanceDisRow && industrialAllowanceDisRow.length > 0) next.industrialAllowanceDisRow = industrialAllowanceDisRow;
        if (furnitureFixtureDisRow && furnitureFixtureDisRow.length > 0) next.furnitureFixtureDisRow = furnitureFixtureDisRow;
        if (computerHardSoftDisRow && computerHardSoftDisRow.length > 0) next.computerHardSoftDisRow = computerHardSoftDisRow;
        if (fullPayMotorDisRow && fullPayMotorDisRow.length > 0) next.fullPayMotorDisRow = fullPayMotorDisRow;
        if (hirePurchaseMotorDisRow && hirePurchaseMotorDisRow.length > 0) next.hirePurchaseMotorDisRow = hirePurchaseMotorDisRow;
        if (machineryDisRow && machineryDisRow.length > 0) next.machineryDisRow = machineryDisRow;
        if (liftEscalatorDisRow && liftEscalatorDisRow.length > 0) next.liftEscalatorDisRow = liftEscalatorDisRow;
        if (equipmentDisRow && equipmentDisRow.length > 0) next.equipmentDisRow = equipmentDisRow;
        if (intangibleRow && intangibleRow.length > 0) next.intangibleRow = intangibleRow;
        if (subsidiaryRow && subsidiaryRow.length > 0) next.subsidiaryRow = subsidiaryRow;
        if (associateRow && associateRow.length > 0) next.associateRow = associateRow;
        if (taxiLicenseDisRow && taxiLicenseDisRow.length > 0) next.taxiLicenseDisRow = taxiLicenseDisRow;
        if (fullPayTaxiCabDisRow && fullPayTaxiCabDisRow.length > 0) next.fullPayTaxiCabDisRow = fullPayTaxiCabDisRow;
        if (hirePurchaseTaxiCabDisRow && hirePurchaseTaxiCabDisRow.length > 0) next.hirePurchaseTaxiCabDisRow = hirePurchaseTaxiCabDisRow;
        if (trustsRow && trustsRow.length > 0) next.trustsRow = trustsRow;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
}
